import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import axios from "axios";
import { API_URL } from "../../../config";
import { mapPropertyOptions, mapUnitTypeOptions, buildMarkSyncedPayload } from "./cubicasaHelpers";

const Field = ({ label, value }) => (
  <div className="mb-2">
    <div className="text-muted" style={{ fontSize: "0.8rem" }}>
      {label}
    </div>
    <div>{value || "-"}</div>
  </div>
);

const CubiCasaOrderDetailModal = ({
  show,
  onHide,
  row,
  token,
  userId,
  onSyncProperty,
  isSyncing,
}) => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [unitTypeOptions, setUnitTypeOptions] = useState([]);
  const [selectedUnitType, setSelectedUnitType] = useState(null);
  const [isUnitTypeLoading, setIsUnitTypeLoading] = useState(false);

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // Reset the Nancy sync selections whenever a different order is opened.
  useEffect(() => {
    setSelectedProperty(null);
    setUnitTypeOptions([]);
    setSelectedUnitType(null);
    setIsUnitTypeLoading(false);
  }, [row && row.logId]);

  // Nancy "Property Name" autosuggest — debounced GetCustomerInfo lookup.
  const fetchProperties = useCallback(
    async (input) => {
      if (!input || input.trim().length < 3) return [];
      try {
        const res = await axios.get(
          `${API_URL}/User/api/CustomerInfo/GetCustomerInfo?PageIndex=1&PageSize=20&userId=${userId}&sort=asc&shipToPropertyName=${encodeURIComponent(
            input.trim()
          )}`,
          authHeader
        );
        return mapPropertyOptions(res?.data?.result);
      } catch (e) {
        return [];
      }
    },
    [authHeader, userId]
  );

  const loadPropertyOptions = useMemo(() => {
    let timer;
    return (input) =>
      new Promise((resolve) => {
        clearTimeout(timer);
        timer = setTimeout(async () => resolve(await fetchProperties(input)), 400);
      });
  }, [fetchProperties]);

  // Nancy "Unit Type" — loaded once a property (contactInfoId) is chosen.
  const handlePropertyChange = async (option) => {
    setSelectedProperty(option);
    setSelectedUnitType(null);
    setUnitTypeOptions([]);
    if (!option) return;
    setIsUnitTypeLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/User/api/User/GetUnitMixDataByContactInfo?PageIndex=1&PageSize=20&sort=asc&ContactInfoId=${option.value}&UserId=${userId}`,
        authHeader
      );
      setUnitTypeOptions(mapUnitTypeOptions(res?.data?.result));
    } catch (e) {
      setUnitTypeOptions([]);
    } finally {
      setIsUnitTypeLoading(false);
    }
  };

  // Build the mark-synced payload and hand it to the container, which dispatches
  // the API request and handles the success/error feedback + list refresh.
  const handleSyncProperty = () => {
    const payload = buildMarkSyncedPayload(row, selectedProperty, selectedUnitType);
    onSyncProperty(payload);
  };

  if (!row) return null;

  // const totalArea = row.totalScannedArea === "" ? "" : `${row.totalScannedArea} sqft`;
  const canSync = Boolean(selectedProperty && selectedUnitType);

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>CubiCasa Order Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {row.isFailed ? (
          <div className="alert alert-danger mb-0">
            <strong>Processing failed.</strong>
            <div>{row.errorMessage || "No further detail available."}</div>
          </div>
        ) : (
          <div className="row">
            <div className="col-md-6 mb-3">
              {row.floorPlanImage ? (
                <img
                  src={row.floorPlanImage}
                  alt="Floor plan"
                  className="img-fluid border rounded"
                  style={{ width: "100%", objectFit: "contain" }}
                />
              ) : (
                <div
                  className="text-muted border rounded d-flex align-items-center justify-content-center"
                  style={{ height: "200px" }}
                >
                  No floor plan image
                </div>
              )}
            </div>
            <div className="col-md-6">
              <Field label="Property Address" value={row.propertyAddress} />
              <Field label="Created At" value={row.createdAtDisplay} />
              {/*<Field label="Created By" value={row.createdBy} />*/}
              <Field label="Property Type" value={row.orderType} />
              {/*<Field label="Total Area" value={totalArea} />*/}
              <Field label="Style Template" value={row.styleTemplateName} />

              <hr />

              <div className="mb-3">
                <label className="form-label" style={{ fontSize: "0.85rem" }}>
                  Nancy Property Name
                </label>
                <AsyncSelect
                  isClearable
                  cacheOptions
                  defaultOptions={false}
                  loadOptions={loadPropertyOptions}
                  value={selectedProperty}
                  onChange={handlePropertyChange}
                  placeholder="Search property name…"
                  noOptionsMessage={({ inputValue }) =>
                    inputValue && inputValue.length >= 2
                      ? "No matching properties"
                      : "Type at least 3 characters"
                  }
                />
              </div>

              <div className="mb-3">
                <label className="form-label" style={{ fontSize: "0.85rem" }}>
                  Nancy Unit Type
                </label>
                <Select
                  isClearable
                  isLoading={isUnitTypeLoading}
                  isDisabled={!selectedProperty}
                  options={unitTypeOptions}
                  value={selectedUnitType}
                  onChange={setSelectedUnitType}
                  placeholder={
                    selectedProperty
                      ? "Select unit type…"
                      : "Select a property first"
                  }
                />
              </div>

              <Button
                variant="success"
                className="w-100"
                disabled={!canSync || isSyncing}
                onClick={handleSyncProperty}
              >
                {isSyncing ? "Syncing…" : "Sync Property"}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CubiCasaOrderDetailModal;
